import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, CheckCircle2, XCircle, Eye, Loader2, Send, Ban, Clock, AlertTriangle, Paperclip, Bot, User as UserIcon, Search, Trash2, Zap, RefreshCw } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import DOMPurify from 'dompurify';
import { AutoApproveRulesDialog } from './AutoApproveRulesDialog';

const STATUS_TABS = [
  { key: 'pending',   label: 'Wartet auf Freigabe', icon: Clock },
  { key: 'sent',      label: 'Versendet',           icon: CheckCircle2 },
  { key: 'failed',    label: 'Fehler',              icon: AlertTriangle },
  { key: 'rejected',  label: 'Abgelehnt',           icon: XCircle },
];

const MAIL_TYPE_LABELS = {
  'registrations.welcome': 'Willkommens-Mail',
  'registrations.confirm': 'Double-Opt-In',
  'registrations.admin_notify': 'Admin-Info Anmeldung',
  'registrations.slot_confirmation': 'Termin-Bestätigung',
};

const mailTypeLabel = (trigger) => MAIL_TYPE_LABELS[trigger] || trigger || null;

export default function MailApprovalPage() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, sent: 0, failed: 0, rejected: 0 });
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);          // single delete confirm
  const [showBulkDelete, setShowBulkDelete] = useState(false); // bulk delete confirm
  const [bulkDeleteTyped, setBulkDeleteTyped] = useState(''); // for >50 rows: type-to-confirm
  const [acting, setActing] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showMonitor, setShowMonitor] = useState(false);
  const [monitorBcc, setMonitorBcc] = useState('');
  const [monitorDraft, setMonitorDraft] = useState('');
  const [monitorSaving, setMonitorSaving] = useState(false);

  const loadMonitorBcc = useCallback(async () => {
    try {
      const r = await api.get('/admin/email-queue/monitor-bcc');
      setMonitorBcc(r.data.email_monitor_bcc || '');
    } catch (_e) { /* */ }
  }, []);

  const saveMonitorBcc = async () => {
    setMonitorSaving(true);
    try {
      const r = await api.put('/admin/email-queue/monitor-bcc', {
        email_monitor_bcc: monitorDraft.trim() || null,
      });
      setMonitorBcc(r.data.email_monitor_bcc || '');
      toast.success(r.data.email_monitor_bcc
        ? `Test-Modus aktiv — BCC an ${r.data.email_monitor_bcc}`
        : 'Test-Modus deaktiviert');
      setShowMonitor(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setMonitorSaving(false);
    }
  };

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/admin/email-queue/summary');
      setSummary(r.data);
    } catch (_e) { /* */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/email-queue', {
        params: { status: tab, search: search || undefined, trigger: triggerFilter || undefined, limit: 100 },
      });
      setItems(r.data.items || []);
      setSelected(new Set());
    } catch (e) {
      toast.error('Liste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [tab, search, triggerFilter]);

  useEffect(() => { loadList(); loadSummary(); }, [loadList, loadSummary]);
  useEffect(() => { loadMonitorBcc(); }, [loadMonitorBcc]);

  const openPreview = async (item) => {
    setPreviewLoading(true);
    setPreviewItem({ id: item.id, subject: item.subject, recipient: item.recipient });
    try {
      const r = await api.get(`/admin/email-queue/${item.id}/preview`);
      setPreviewItem(r.data);
    } catch (e) {
      toast.error('Vorschau konnte nicht geladen werden');
      setPreviewItem(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const approve = async (id, closePreview = true) => {
    setActing(true);
    try {
      const r = await api.post(`/admin/email-queue/${id}/approve`);
      if (r.data.status === 'sent') toast.success('E-Mail freigegeben und versendet');
      else if (r.data.status === 'failed') toast.error(`SMTP-Fehler: ${r.data.error_message}`);
      await loadList(); await loadSummary();
      if (closePreview) setPreviewItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Freigabe fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const retry = async (id) => {
    setActing(true);
    try {
      const r = await api.post(`/admin/email-queue/${id}/retry`);
      if (r.data.status === 'sent') toast.success('E-Mail erfolgreich erneut versendet');
      else toast.error(`SMTP-Fehler: ${r.data.error_message}`);
      await loadList(); await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erneut senden fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    if (!rejectItem) return;
    setActing(true);
    try {
      await api.post(`/admin/email-queue/${rejectItem.id}/reject`, { reason: rejectReason || null });
      toast.success('E-Mail abgelehnt');
      await loadList(); await loadSummary();
      setRejectItem(null);
      setRejectReason('');
      setPreviewItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ablehnen fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} E-Mail(s) freigeben und senden?`)) return;
    setActing(true);
    try {
      const r = await api.post('/admin/email-queue/bulk-approve', { ids: Array.from(selected) });
      toast.success(`${r.data.succeeded.length} versendet, ${r.data.failed.length} fehlgeschlagen`);
      await loadList(); await loadSummary();
    } catch (e) {
      toast.error('Bulk-Freigabe fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const deleteOne = async () => {
    if (!deleteItem) return;
    setActing(true);
    try {
      await api.delete(`/admin/email-queue/${deleteItem.id}`);
      toast.success('E-Mail aus Queue gelöscht');
      await loadList(); await loadSummary();
      setDeleteItem(null);
      setPreviewItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    // Schutz vor Massenlöschungen: ab 50 Rows muss „LÖSCHEN" eingetippt werden
    if (selected.size > 50 && bulkDeleteTyped.trim().toUpperCase() !== 'LÖSCHEN') {
      toast.error('Bitte "LÖSCHEN" eintippen, um die Massenlöschung zu bestätigen');
      return;
    }
    setActing(true);
    try {
      const r = await api.post('/admin/email-queue/bulk-delete', { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} E-Mail(s) gelöscht`);
      await loadList(); await loadSummary();
      setShowBulkDelete(false);
      setBulkDeleteTyped('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk-Löschen fehlgeschlagen');
    } finally {
      setActing(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="mail-approval-page">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-brand-red" /> E-Mail-Freigabe
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Alle ausgehenden E-Mails (auch System-Mails) landen hier zur Super-Admin-Freigabe.
              Erst nach „Freigeben" geht die Mail über SMTP an den Empfänger.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => { setMonitorDraft(monitorBcc); setShowMonitor(true); }}
              data-testid="mail-approval-monitor-btn"
            >
              <Eye className={`w-4 h-4 mr-2 ${monitorBcc ? 'text-emerald-600' : 'text-slate-400'}`} /> Test-Modus
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowRules(true)}
              data-testid="mail-approval-rules-btn"
            >
              <Zap className="w-4 h-4 mr-2 text-brand-red" /> Auto-Freigabe
            </Button>
          </div>
        </div>

        {monitorBcc && (
          <div className="mb-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg px-4 py-2 text-sm" data-testid="mail-approval-monitor-banner">
            <Eye className="w-4 h-4 shrink-0" />
            Test-Modus aktiv: Jede versendete Mail dieses Mandanten geht zusätzlich als BCC an <strong>{monitorBcc}</strong>.
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="mail-approval-tabs">
            {STATUS_TABS.map((t) => {
              const count = summary[t.key] ?? 0;
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`mail-approval-tab-${t.key}`}>
                  <Icon className="w-4 h-4 mr-1" />
                  {t.label}
                  <Badge variant="secondary" className="ml-2" data-testid={`mail-approval-count-${t.key}`}>{count}</Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Suche nach Empfänger oder Betreff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="mail-approval-search"
            />
          </div>
          <select
            value={triggerFilter}
            onChange={(e) => setTriggerFilter(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
            data-testid="mail-approval-trigger-filter"
          >
            <option value="">Alle E-Mail-Typen</option>
            {Object.entries(MAIL_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
            <option value="untagged">Ohne E-Mail-Typ</option>
          </select>
          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              {tab === 'pending' && (
                <Button
                  onClick={bulkApprove}
                  disabled={acting}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="mail-approval-bulk-approve-btn"
                >
                  {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  {selected.size} freigeben
                </Button>
              )}
              <Button
                onClick={() => setShowBulkDelete(true)}
                disabled={acting}
                variant="outline"
                className="text-rose-600 border-rose-300 hover:bg-rose-50"
                data-testid="mail-approval-bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {selected.size} löschen
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="w-8 h-8 mx-auto animate-spin" />
              <div className="mt-2 text-sm">Laden...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="mail-approval-empty">
              <Mail className="w-8 h-8 mx-auto opacity-30 mb-2" />
              <div className="text-sm">Keine E-Mails in „{STATUS_TABS.find(t => t.key === tab)?.label}"</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 w-10">
                    <Checkbox
                      checked={selected.size === items.length && items.length > 0}
                      onCheckedChange={selectAll}
                      data-testid="mail-approval-select-all"
                    />
                  </th>
                  <th className="p-3 text-left">Empfänger</th>
                  <th className="p-3 text-left">Betreff</th>
                  <th className="p-3 text-left">E-Mail-Typ</th>
                  <th className="p-3 text-left">Quelle</th>
                  <th className="p-3 text-left">Zeitpunkt</th>
                  <th className="p-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`mail-approval-row-${item.id}`}>
                    <td className="p-3">
                      <Checkbox
                        checked={selected.has(item.id)}
                        onCheckedChange={() => toggleSelect(item.id)}
                        data-testid={`mail-approval-checkbox-${item.id}`}
                      />
                    </td>
                    <td className="p-3 font-mono text-xs">{item.recipient}</td>
                    <td className="p-3 font-medium">
                      {item.subject}
                      {item.attachments_count > 0 && (
                        <Badge variant="secondary" className="ml-2 gap-1">
                          <Paperclip className="w-3 h-3" /> {item.attachments_count}
                        </Badge>
                      )}
                      {item.auto_approved && (
                        <Badge className="ml-2 gap-1 bg-amber-50 text-amber-700 border border-amber-200 hover:bg-brand-red/5" data-testid={`mail-approval-auto-badge-${item.id}`}>
                          <Zap className="w-3 h-3" /> Auto
                        </Badge>
                      )}
                    </td>
                    <td className="p-3" data-testid={`mail-approval-mailtype-${item.id}`}>
                      {mailTypeLabel(item.trigger_source) ? (
                        <Badge variant="outline" className="text-xs font-normal text-slate-600 border-slate-300">
                          {mailTypeLabel(item.trigger_source)}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-slate-600">
                      {item.is_system_generated
                        ? <span className="inline-flex items-center gap-1"><Bot className="w-3 h-3 text-slate-500" /> System</span>
                        : <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3 text-brand-red" /> Manuell</span>}
                    </td>
                    <td className="p-3 text-xs text-slate-500">
                      {formatDateTime(item.created_at)}
                    </td>
                    <td className="p-3 text-right space-x-1">
                      <Button variant="outline" size="sm" onClick={() => openPreview(item)} data-testid={`mail-approval-preview-${item.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Vorschau
                      </Button>
                      {item.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => approve(item.id, false)}
                            disabled={acting}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`mail-approval-approve-${item.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Freigeben
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRejectItem(item)}
                            data-testid={`mail-approval-reject-${item.id}`}
                            className="text-rose-600 border-rose-300 hover:bg-rose-50"
                          >
                            <Ban className="w-3 h-3 mr-1" /> Ablehnen
                          </Button>
                        </>
                      )}
                      {item.status === 'failed' && (
                        <Button
                          size="sm"
                          onClick={() => retry(item.id)}
                          disabled={acting}
                          className="bg-brand-red hover:bg-brand-red/90"
                          data-testid={`mail-approval-retry-${item.id}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Erneut senden
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteItem(item)}
                        data-testid={`mail-approval-delete-${item.id}`}
                        className="text-rose-700 border-rose-300 hover:bg-rose-50"
                        title="Komplett löschen"
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Löschen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Preview-Dialog */}
        <Dialog open={!!previewItem} onOpenChange={(o) => { if (!o) setPreviewItem(null); }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="mail-approval-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-brand-red" /> E-Mail-Vorschau
              </DialogTitle>
              <DialogDescription>
                So wird die E-Mail beim Empfänger aussehen.
              </DialogDescription>
            </DialogHeader>
            {previewLoading ? (
              <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : previewItem && previewItem.html_body !== undefined ? (
              <div className="overflow-y-auto flex-1 space-y-3">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs space-y-1">
                  <div><strong>Von:</strong> {previewItem.sender_name} &lt;{previewItem.sender_email}&gt;</div>
                  <div><strong>An:</strong> <span className="font-mono" data-testid="mail-approval-preview-recipient">{previewItem.recipient}</span></div>
                  {previewItem.cc && <div><strong>CC:</strong> {previewItem.cc}</div>}
                  <div><strong>Betreff:</strong> <span className="font-medium">{previewItem.subject}</span></div>
                  {previewItem.trigger_source && (
                    <div><strong>E-Mail-Typ:</strong> {mailTypeLabel(previewItem.trigger_source)}
                      {previewItem.is_system_generated ? ' (System)' : ' (Manuell)'}</div>
                  )}
                  {previewItem.attachments && previewItem.attachments.length > 0 && (
                    <div><strong>Anhänge:</strong> {previewItem.attachments.map((a, i) =>
                      <Badge key={i} variant="secondary" className="ml-1 gap-1">
                        <Paperclip className="w-3 h-3" /> {a.filename} ({Math.round((a.size || 0) / 1024)} KB)
                      </Badge>)}
                    </div>
                  )}
                </div>
                <div className="border-2 border-slate-200 rounded-lg p-4 bg-white">
                  <div
                    className="prose prose-sm max-w-none"
                    data-testid="mail-approval-preview-html"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewItem.html_body || '') }}
                  />
                </div>
                {previewItem.status !== 'pending' && (
                  <div className={`rounded-lg p-3 text-xs ${
                    previewItem.status === 'sent' ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' :
                    previewItem.status === 'rejected' ? 'bg-rose-50 border border-rose-200 text-rose-900' :
                    previewItem.status === 'failed' ? 'bg-amber-50 border border-amber-200 text-amber-900' :
                    'bg-slate-50 border border-slate-200 text-slate-700'
                  }`}>
                    <strong>Status:</strong> {statusLabel(previewItem.status)}
                    {previewItem.rejection_reason && <div>Grund: {previewItem.rejection_reason}</div>}
                    {previewItem.error_message && <div>Fehler: {previewItem.error_message}</div>}
                  </div>
                )}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewItem(null)}>Schließen</Button>
              {previewItem?.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setRejectItem(previewItem)}
                    className="text-rose-600 border-rose-300 hover:bg-rose-50"
                    data-testid="mail-approval-preview-reject-btn"
                  >
                    <Ban className="w-4 h-4 mr-1" /> Ablehnen
                  </Button>
                  <Button
                    onClick={() => approve(previewItem.id, true)}
                    disabled={acting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="mail-approval-preview-approve-btn"
                  >
                    {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Freigeben & Senden
                  </Button>
                </>
              )}
              {previewItem && previewItem.id && (
                <Button
                  variant="outline"
                  onClick={() => setDeleteItem(previewItem)}
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                  data-testid="mail-approval-preview-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Löschen
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject-Dialog */}
        <Dialog open={!!rejectItem} onOpenChange={(o) => { if (!o) { setRejectItem(null); setRejectReason(''); } }}>
          <DialogContent data-testid="mail-approval-reject-dialog">
            <DialogHeader>
              <DialogTitle>E-Mail ablehnen</DialogTitle>
              <DialogDescription>
                An: {rejectItem?.recipient} · Betreff: {rejectItem?.subject}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder={'Optional: Grund der Ablehnung (z.B. „Tippfehler im Betreff — neu senden")'}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              data-testid="mail-approval-reject-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectItem(null)}>Abbrechen</Button>
              <Button
                onClick={reject}
                disabled={acting}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="mail-approval-reject-confirm"
              >
                {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Ablehnen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Delete-Confirm-Dialog (single) */}
        <Dialog open={!!deleteItem} onOpenChange={(o) => { if (!o) setDeleteItem(null); }}>
          <DialogContent data-testid="mail-approval-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <Trash2 className="w-5 h-5" /> E-Mail komplett löschen?
              </DialogTitle>
              <DialogDescription>
                Diese E-Mail wird unwiderruflich aus der Queue entfernt.
                Sie taucht in keiner Statistik mehr auf.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-50 rounded p-3 text-sm space-y-1 border border-slate-200">
              <div><strong>An:</strong> <span className="font-mono">{deleteItem?.recipient}</span></div>
              <div><strong>Betreff:</strong> {deleteItem?.subject}</div>
              <div><strong>Status:</strong> {statusLabel(deleteItem?.status)}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteItem(null)}>Abbrechen</Button>
              <Button
                onClick={deleteOne}
                disabled={acting}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="mail-approval-delete-confirm"
              >
                {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Endgültig löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk-Delete-Confirm-Dialog */}
        <Dialog open={showBulkDelete} onOpenChange={(o) => { if (!o) { setShowBulkDelete(false); setBulkDeleteTyped(''); } }}>
          <DialogContent data-testid="mail-approval-bulk-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <Trash2 className="w-5 h-5" /> {selected.size} E-Mail(s) löschen?
              </DialogTitle>
              <DialogDescription>
                Alle ausgewählten E-Mails werden unwiderruflich aus der Queue entfernt
                — strikt nur in deinem aktiven Mandanten.
              </DialogDescription>
            </DialogHeader>
            {selected.size > 50 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm space-y-2">
                <div className="flex items-center gap-2 text-amber-900 font-semibold">
                  <AlertTriangle className="w-4 h-4" />
                  Massenlöschung — bitte „LÖSCHEN" eintippen, um zu bestätigen:
                </div>
                <Input
                  value={bulkDeleteTyped}
                  onChange={(e) => setBulkDeleteTyped(e.target.value)}
                  placeholder="LÖSCHEN"
                  data-testid="mail-approval-bulk-delete-typed"
                  className="bg-white"
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowBulkDelete(false); setBulkDeleteTyped(''); }}>Abbrechen</Button>
              <Button
                onClick={bulkDelete}
                disabled={acting || (selected.size > 50 && bulkDeleteTyped.trim().toUpperCase() !== 'LÖSCHEN')}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="mail-approval-bulk-delete-confirm"
              >
                {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                {selected.size} endgültig löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AutoApproveRulesDialog open={showRules} onOpenChange={setShowRules} />

        {/* Test-Modus (Monitor-BCC) Dialog */}
        <Dialog open={showMonitor} onOpenChange={setShowMonitor}>
          <DialogContent className="max-w-md" data-testid="mail-approval-monitor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-emerald-600" /> Test-Modus (BCC-Mitlesen)
              </DialogTitle>
              <DialogDescription>
                Solange eine Adresse gesetzt ist, geht jede tatsächlich versendete Mail dieses
                Mandanten zusätzlich als Blindkopie (BCC) an diese Adresse — ideal, um vor der
                Vollautomatisierung echte Mails mitzulesen. Feld leeren = Test-Modus aus.
              </DialogDescription>
            </DialogHeader>
            <div>
              <Input
                type="email"
                placeholder="z. B. mail-kontrolle@musikakademie.de"
                value={monitorDraft}
                onChange={(e) => setMonitorDraft(e.target.value)}
                data-testid="mail-approval-monitor-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMonitor(false)} data-testid="mail-approval-monitor-cancel">Abbrechen</Button>
              <Button onClick={saveMonitorBcc} disabled={monitorSaving} data-testid="mail-approval-monitor-save">
                {monitorSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
